import { tracker } from '@/directives/v-scene';
import { useWindowScroll } from '@vueuse/core';
import {
  interpolateDistortions,
  normalizeXY,
  denormalizeXY,
  rotatePoint,
  distortA,
  distortB,
  squareDistortion,
  squareStarDistortion,
  //squareFrameDistortion,
  squareDistortionCleaner,
  soundDistortion,
  gaussianDistortion,
  circularDistortion,
  movement
} from './distortions';
import { distortAOrganic } from './distortions/distortAOrganic';
import { distortAScatter } from './distortions/distortAScatter';
import { distortAScatterWide } from './distortions/distortAScatterWide';

// Access reactive data from the tracker
const topScenes = tracker.topScenes;
const { y: windowScrollY } = useWindowScroll();

// Named distortion library - maps names to distortion functions
export const distortionLibrary = {
  'none': (x, y) => ({ x, y }),
  'distortA': (x, y, width, height) => distortA(x, y, width, height, 2000),
  'distortAMedium': (x, y, width, height) => movement(distortA(x, y, width, height, 2000, 0.5, 0.9), 10),
  'distortALight': (x, y, width, height, time, scroll) => distortA(x, y, width, height, time, 0.5, 0.8, scroll),
  'distortAWave': (x, y, width, height, time, scroll) => distortA(x, y, width, height, time, 0.1, 0.5, scroll),
  'distortAOrganic': (x, y, width, height, time, scroll) => interpolateDistortions(distortA(x, y, width, height, time, 0.5, 0.8, scroll), distortAOrganic(x, y, width, height, time, scroll), 0.6),
  'distortAScatter': (x, y, width, height, time, scroll) => distortAScatter(x, y, width, height, time, scroll),
  'distortAScatterWide': (x, y, width, height, time, scroll) => distortAScatterWide(x, y, width, height, time, scroll),
  'blend': (x, y, width, height) => interpolateDistortions({ x, y }, circularDistortion(x, y, width, height, 0, 0.66, 0), 0.3),
  'square': (x, y, width, height, time) =>   squareDistortion(x, y, width, height, 0.8, 9.5, time),
  'distortB': (x, y, width, height) => distortB(x, y, width, height),
  'blendLight': (x, y, width, height) => interpolateDistortions({ x, y }, distortA(x, y, width, height, 2000), 0.4),
  'circle': (x, y, width, height, time) => circularDistortion(x, y, width, height, time),
  'squareStar': (x, y, width, height) => squareStarDistortion(x, y, width, height),
  //'squareFrame': (x, y, width, height) => squareFrameDistortion(x, y, width, height),
  'cleanSquare': (x, y, width, height) => squareDistortionCleaner(x, y, width, height),
  'gaussian': (x, y, width, height) => gaussianDistortion(x, y, width, height)
};

// Main distortion function
export const distort = (x, y, width, height, time = 1, blend = 0.5, soundSpectrum = [0,0,0,0]) => {
  // Get active scenes from the tracker
  const scenes = topScenes.value;

  // Use window scroll Y position
  const scrollValue = windowScrollY.value;

  // If no scenes, return original position
  if (!scenes || scenes.length === 0) {
    return { x, y };
  }

  // Choose distortion function based on element ID (as string)
  const getDistortionResult = (scene) => {
    const distortName = scene?.id || 'none';
    const distortFn = distortionLibrary[distortName] || distortionLibrary.none;
    return distortFn(x, y, width, height, time, scrollValue);
  };

  // Calculate weighted blend of all active scenes
  let blendedX = 0;
  let blendedY = 0;

  scenes.forEach(scene => {
    const result = getDistortionResult(scene);
    blendedX += result.x * scene.weight;
    blendedY += result.y * scene.weight;
  });

  const interpolatedDistortions = { x: blendedX, y: blendedY };

  // Apply sound distortion and normalize
  const normalized = normalizeXY(interpolatedDistortions, width, height);
  const distorted = soundDistortion(normalized, width, height, soundSpectrum, x, y);
  const finalPosition = denormalizeXY(distorted, width, height);

  return finalPosition;
};
